import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/profile/Avatar'
import { CategoryChip } from '@/components/post/CategoryChip'
import type { Group } from '@olimhub/shared'

interface GroupCardProps {
  group: Group
}

// Row layout mirrors AccountCard.tsx (avatar + title + secondary line).
// Closed pill mirrors the same Lock + groups.closed treatment used on the
// group's own profile header (GroupPage.tsx) — metadata (name, description,
// existence) stays visible even for a closed group; only its content is
// gated elsewhere.
export function GroupCard({ group }: GroupCardProps) {
  const { t } = useTranslation()

  return (
    <Link
      to={`/groups/${group.slug}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        textDecoration: 'none',
      }}
    >
      <Avatar name={group.name} avatarUrl={group.avatarUrl} accountType="group" size="md" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              color: 'var(--text-strong)',
              fontSize: 'var(--fs-body)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {group.name}
          </div>
          {!group.isOpen && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--surface-2)',
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              <Lock size={12} /> {t('groups.closed')}
            </span>
          )}
        </div>
        {group.description && (
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {group.description}
          </div>
        )}
        {(group.category || group.distanceKm !== undefined) && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            {group.category && <CategoryChip category={group.category} />}
            {group.distanceKm !== undefined && (
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                {t('search.location_distance', { km: group.distanceKm })}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  )
}
